import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  PatternlibButton,
  PatternlibOverlayContainer,
  PatternlibTextlink,
  PatternlibIcon,
  PatternlibTextinput,
} from "@liebherr/patternlib-react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import styles from "./KeyButton.module.css"; // Import css modules stylesheet as styles
import keyIcon from "../../img/liebherr-key-icon.svg";
import InfoButton from "../info-button/InfoButton";
import Separator from "../separator/Separator";
import { pushToDataLayer } from "../../helpers/helpers";
import useMutationObserver from "../../hooks/useMutationObserver";

const KeyButton = ({ partkey, onPress }) => {
  const { t, i18n } = useTranslation();
  const [show, setShow] = useState(false);
  const inputValue = useRef();
  const overlayRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();

  const insertProductKeyGA = (productKey) => {
    const eventName = "insert_product_key";
    const pagePath = location.pathname;
    const pageType = "configurator";
    const language =
      i18n.language === "de-DE" || i18n.language === "de" ? "de-DE" : "en-EN";
    const product_key = productKey;
    let props = { pagePath, pageType, language, product_key };

    pushToDataLayer(eventName, props);
  };

  // use mutation observer to get current screen size
  const options = {
    config: { attributes: true, childList: true, subtree: true },
  };
  const target = document.documentElement;
  const attributeName = "breakpoint";
  const screen = useMutationObserver(options, target, attributeName);

  const searchForPart = () => {
    const value = inputValue.current;
    const input = value.trim();
    insertProductKeyGA(input);
    if (input !== undefined) {
      const strTyp = input.substring(0, 3);
      sessionStorage.clear();
      switch (strTyp) {
        case "S26":
          // console.log("Load series 260");
          navigate("/series260", { state: { key: input } });
          break;
        case "S38":
          // console.log("Load series 380");
          navigate("/series380", { state: { key: input } });
          break;
        case "ISO":
          // console.log("Load series iso 6022");
          navigate("/iso6022", { state: { key: input } });
          break;
        default:
          navigate("/series260", { state: { key: input } });
      }
    }
  };

  const onButtonClick = () => {
    setShow(true);
  };

  const onConfirm = () => {
    setShow(false);
    searchForPart();
  };

  const onClose = () => {
    setShow(false);
  };

  useEffect(() => {
    if (show) {
      const ref = overlayRef.current;
      const element = ref.shadowRoot.querySelector(".backdrop");
      const content = ref.shadowRoot.querySelector(".content");
      content.style.zIndex = "9999";

      if (element !== null) {
        element.addEventListener("click", onClose, false);

        return () => {
          element.removeEventListener("click", onClose, true);
        };
      }
    }
  }, [show]);

  const onChangeListener = (event) => {
    if (event.detail?.value !== undefined) {
      inputValue.current = event.detail.value;
    }
  };

  return (
    <div className={styles.keyButton}>
      {screen !== "isMobile" && (
        <div className={`${styles.grid} ${styles.titleWrapper}`}>
          <img src={keyIcon} width="32px" alt="img"></img>
          <span>{t("infotexts.general.key.title")}</span>
        </div>
      )}
      <div className={`${styles.grid} ${styles.partWrapper}`}>
        <span>{partkey}</span>
        <span className={`${styles.grid} ${styles.buttonWrapper}`}>
          <PatternlibButton
            type="primary"
            size="small"
            label={t("KeyButton.copy")}
            onClick={onPress}
          ></PatternlibButton>
          <InfoButton
            title={t("infotexts.general.key.title")}
            abbreviation={"key"}
            path={"general"}
            series={partkey.substring(0, 3)}
          ></InfoButton>
        </span>
      </div>
      <Separator space="xxs"></Separator>
      <div className={styles.link}>
        <span> {t("start.haveKey")}</span>
        <div className={styles.overlayContainer}>
          <PatternlibOverlayContainer
            target="center"
            show={show}
            disable-click-handlers
            ref={overlayRef}
          >
            <PatternlibTextlink
              label={t("configurator.affordanceToInput")}
              size="small"
              onClick={onButtonClick}
              slot="element"
            ></PatternlibTextlink>
            <div slot="content" className={styles.modalContent}>
              <div className={styles.modalHeader}>
                <h2>{t("KeyButton.title")}</h2>
                <div className={styles.closeIcon}>
                  <PatternlibIcon
                    icon-name="x"
                    onClick={onClose}
                  ></PatternlibIcon>
                </div>
              </div>
              <PatternlibTextinput
                placeholder={t("start.enterKey")}
                onLhChange={(e) => onChangeListener(e)}
              ></PatternlibTextinput>
              <div className={styles.modalFooter}>
                <PatternlibTextlink
                  label={t("KeyButton.cancelButton")}
                  size="small"
                  onClick={onClose}
                ></PatternlibTextlink>
                <PatternlibButton
                  label-text-align="left"
                  icon-position="right"
                  label={t("KeyButton.confirmButton")}
                  size="small"
                  onClick={onConfirm}
                >
                  <PatternlibIcon
                    icon-name="font-arrow"
                    slot="icon"
                  ></PatternlibIcon>
                </PatternlibButton>
              </div>
            </div>
          </PatternlibOverlayContainer>
        </div>
      </div>
    </div>
  );
};

KeyButton.propTypes = {
  partkey: PropTypes.string,
  onPress: PropTypes.func.isRequired,
};

export default KeyButton;
